import React, { useState, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '../hooks/redux';
import { getUserWallet, updateBankDetails, requestWithdrawal, getUserTransactions, getUserWithdrawals, calculateTaskEarnings, Transaction, WithdrawalRequest } from '../features/wallet/walletSlice';
import { showToast } from '../features/ui/uiSlice';
import { RootState } from '../store/store';
import {
    WalletIcon,
    CreditCardIcon,
    ArrowUpTrayIcon,
    ArrowDownTrayIcon,
    BanknotesIcon,
    ClockIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon,
    ChartBarIcon,
    BuildingLibraryIcon,
    UserIcon,
    CalendarIcon,
    CurrencyDollarIcon
} from '@heroicons/react/24/outline';

const WalletManagement: React.FC = () => {
    const dispatch = useAppDispatch();
    const { wallet, transactions, withdrawals, isLoading } = useAppSelector((state: RootState) => state.wallet);
    const [activeTab, setActiveTab] = useState<'overview' | 'transactions' | 'withdrawals' | 'bank'>('overview');
    const [showWithdrawalForm, setShowWithdrawalForm] = useState(false);
    const [showBankForm, setShowBankForm] = useState(false);
    const [taskEarnings, setTaskEarnings] = useState<any[]>([]);
    const [withdrawalData, setWithdrawalData] = useState({
        amount: '',
        bankDetails: {
            accountNumber: '',
            bankName: '',
            ifscCode: '',
            accountHolderName: '',
        }
    });
    const [bankData, setBankData] = useState({
        accountNumber: '',
        bankName: '',
        ifscCode: '',
        branchName: '',
        accountHolderName: '',
    });

    useEffect(() => {
        dispatch(getUserWallet());
        dispatch(getUserTransactions({}));
        dispatch(getUserWithdrawals({}));

        // Calculate task earnings
        dispatch(calculateTaskEarnings()).then((result) => {
            if (calculateTaskEarnings.fulfilled.match(result)) {
                setTaskEarnings(result.payload.taskEarnings || []);
            }
        });
    }, [dispatch]);

    const handleWithdrawalSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!wallet?.bankDetails?.isVerified) {
            dispatch(showToast({
                message: 'Bank details must be verified before requesting withdrawal',
                type: 'error'
            }));
            return;
        }

        const amount = parseFloat(withdrawalData.amount);
        const availableForWithdrawal = (wallet?.currentBalance || 0) - (wallet?.withdrawalRequests?.filter(req => req.status === 'pending' || req.status === 'approved').reduce((sum, req) => sum + req.amount, 0) || 0);

        if (amount > availableForWithdrawal) {
            dispatch(showToast({
                message: 'Withdrawal amount cannot exceed available balance',
                type: 'error'
            }));
            return;
        }

        try {
            await dispatch(requestWithdrawal({
                amount: amount,
                bankDetails: wallet.bankDetails // Use verified bank details automatically
            })).unwrap();

            dispatch(showToast({
                message: 'Withdrawal request submitted successfully!',
                type: 'success'
            }));

            setShowWithdrawalForm(false);
            setWithdrawalData({
                amount: '',
                bankDetails: {
                    accountNumber: '',
                    bankName: '',
                    ifscCode: '',
                    accountHolderName: '',
                }
            });

            // Wallet state is updated by Redux reducer from withdrawal response
            // No need to call getUserWallet() again
        } catch (error: any) {
            dispatch(showToast({
                message: error || 'Failed to submit withdrawal request',
                type: 'error'
            }));
        }
    };

    const handleBankDetailsSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        try {
            await dispatch(updateBankDetails(bankData)).unwrap();

            dispatch(showToast({
                message: 'Bank details updated successfully! Awaiting admin verification.',
                type: 'success'
            }));

            setShowBankForm(false);
            dispatch(getUserWallet());
        } catch (error: any) {
            dispatch(showToast({
                message: error || 'Failed to update bank details',
                type: 'error'
            }));
        }
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex justify-center items-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600 dark:text-gray-400 text-lg">Loading your wallet...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            <div className="container mx-auto px-4 py-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="p-3 bg-blue-100 dark:bg-blue-900/20 rounded-xl">
                            <WalletIcon className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Wallet Management</h1>
                            <p className="text-gray-600 dark:text-gray-400">Manage your earnings, withdrawals, and bank details</p>
                        </div>
                    </div>
                </div>

                {/* Tab Navigation */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 mb-8">
                    <nav className="flex space-x-1 p-2">
                        {[
                            { id: 'overview', label: 'Overview', icon: ChartBarIcon },
                            { id: 'transactions', label: 'Transactions', icon: CreditCardIcon },
                            { id: 'withdrawals', label: 'Withdrawals', icon: ArrowUpTrayIcon },
                            { id: 'bank', label: 'Bank Details', icon: BuildingLibraryIcon },
                        ].map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id as any)}
                                className={`flex items-center gap-2 px-4 py-3 rounded-xl font-medium text-sm transition-all duration-200 ${activeTab === tab.id
                                    ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 shadow-sm'
                                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-700'
                                    }`}
                            >
                                <tab.icon className="h-5 w-5" />
                                {tab.label}
                            </button>
                        ))}
                    </nav>
                </div>

                {/* Overview Tab */}
                {activeTab === 'overview' && wallet && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                        <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 p-6 rounded-2xl shadow-sm border border-green-200 dark:border-green-800">
                            <div className="flex items-center justify-between mb-4">
                                <div className="p-3 bg-green-100 dark:bg-green-800/30 rounded-xl">
                                    <CurrencyDollarIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
                                </div>
                                <span className="text-sm font-medium text-green-600 dark:text-green-400">Available</span>
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Available for Withdrawal</h3>
                            <p className="text-3xl font-bold text-green-600 dark:text-green-400">
                                ₹{((wallet.currentBalance || 0) - (wallet.withdrawalRequests?.filter(req => req.status === 'pending' || req.status === 'approved').reduce((sum, req) => sum + req.amount, 0) || 0)).toLocaleString()}
                            </p>
                            <p className="text-sm text-green-600/70 dark:text-green-400/70 mt-1">Ready for withdrawal</p>
                        </div>

                        <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 p-6 rounded-2xl shadow-sm border border-blue-200 dark:border-blue-800">
                            <div className="flex items-center justify-between mb-4">
                                <div className="p-3 bg-blue-100 dark:bg-blue-800/30 rounded-xl">
                                    <CheckCircleIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                                </div>
                                <span className="text-sm font-medium text-blue-600 dark:text-blue-400">Verified</span>
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Verified Balance</h3>
                            <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">₹{wallet.verifiedBalance?.toLocaleString() || '0'}</p>
                            <p className="text-sm text-blue-600/70 dark:text-blue-400/70 mt-1">Validated earnings</p>
                        </div>

                        <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 dark:from-yellow-900/20 dark:to-yellow-800/20 p-6 rounded-2xl shadow-sm border border-yellow-200 dark:border-yellow-800">
                            <div className="flex items-center justify-between mb-4">
                                <div className="p-3 bg-yellow-100 dark:bg-yellow-800/30 rounded-xl">
                                    <ClockIcon className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
                                </div>
                                <span className="text-sm font-medium text-yellow-600 dark:text-yellow-400">Pending</span>
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Pending Balance</h3>
                            <p className="text-3xl font-bold text-yellow-600 dark:text-yellow-400">₹{wallet.pendingBalance?.toLocaleString() || '0'}</p>
                            <p className="text-sm text-yellow-600/70 dark:text-yellow-400/70 mt-1">Awaiting validation</p>
                        </div>

                        <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 p-6 rounded-2xl shadow-sm border border-purple-200 dark:border-purple-800">
                            <div className="flex items-center justify-between mb-4">
                                <div className="p-3 bg-purple-100 dark:bg-purple-800/30 rounded-xl">
                                    <BanknotesIcon className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                                </div>
                                <span className="text-sm font-medium text-purple-600 dark:text-purple-400">Total</span>
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Total Earnings</h3>
                            <p className="text-3xl font-bold text-purple-600 dark:text-purple-400">₹{wallet.totalEarnings?.toLocaleString() || '0'}</p>
                            <p className="text-sm text-purple-600/70 dark:text-purple-400/70 mt-1">All-time earnings</p>
                        </div>
                    </div>
                )}



                {/* Task Earnings Section */}
                {activeTab === 'overview' && (
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 mb-8">
                        <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                                        <ChartBarIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                                    </div>
                                    <div>
                                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Task Earnings Breakdown</h2>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">Detailed breakdown of your earnings from completed tasks</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => {
                                        dispatch(calculateTaskEarnings()).then((result) => {
                                            if (calculateTaskEarnings.fulfilled.match(result)) {
                                                setTaskEarnings(result.payload.taskEarnings || []);
                                            }
                                        });
                                    }}
                                    disabled={isLoading}
                                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all duration-200 disabled:opacity-50 shadow-sm"
                                >
                                    <ChartBarIcon className="h-4 w-4" />
                                    {isLoading ? 'Calculating...' : 'Refresh Earnings'}
                                </button>
                            </div>
                        </div>
                        <div className="p-6">
                            {taskEarnings.length > 0 ? (
                                <div className="space-y-3">
                                    {taskEarnings.map((task, index) => (
                                        <div key={index} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                                            <div className="flex items-center gap-4">
                                                <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                                                    <ChartBarIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                                                </div>
                                                <div>
                                                    <h4 className="font-semibold text-gray-900 dark:text-white">{task.taskTitle}</h4>
                                                    <p className="text-sm text-gray-600 dark:text-gray-400">{task.company}</p>
                                                    <div className="flex items-center gap-2 mt-1">
                                                        <span className="text-xs text-gray-500 dark:text-gray-400">
                                                            {task.designation?.replace('_', ' ')} • {task.dataCount} entries
                                                        </span>
                                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${task.status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' :
                                                            task.status === 'completed' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400' :
                                                                'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400'
                                                            }`}>
                                                            {task.status}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-xl font-bold text-green-600 dark:text-green-400">
                                                    ₹{task.earnings.toLocaleString()}
                                                </p>
                                                <p className="text-sm text-gray-500 dark:text-gray-400">Earnings</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-12">
                                    <div className="p-4 bg-gray-100 dark:bg-gray-700 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                                        <ChartBarIcon className="h-8 w-8 text-gray-400" />
                                    </div>
                                    <p className="text-gray-600 dark:text-gray-400 text-lg font-medium">No task earnings found</p>
                                    <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">Complete tasks and submit data to earn money</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Transactions Tab */}
                {activeTab === 'transactions' && (
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                        <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                                    <CreditCardIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                                </div>
                                <div>
                                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Transaction History</h2>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">All your wallet transactions and earnings</p>
                                </div>
                            </div>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                <thead className="bg-gray-50 dark:bg-gray-700">
                                    <tr>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Type
                                        </th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Amount
                                        </th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Description
                                        </th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Date
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                    {transactions.map((transaction: Transaction) => (
                                        <tr key={transaction._id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center gap-2">
                                                    {transaction.type === 'earning' ? (
                                                        <ArrowDownTrayIcon className="h-4 w-4 text-green-600" />
                                                    ) : (
                                                        <ArrowUpTrayIcon className="h-4 w-4 text-red-600" />
                                                    )}
                                                    <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${transaction.type === 'earning' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' :
                                                        transaction.type === 'withdrawal' ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400' :
                                                            'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400'
                                                        }`}>
                                                        {transaction.type.replace('_', ' ')}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`text-sm font-semibold ${transaction.type === 'earning' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                                    {transaction.type === 'earning' ? '+' : '-'}₹{transaction.amount?.toLocaleString() || '0'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                                                {transaction.description}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${transaction.status === 'completed' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' :
                                                    transaction.status === 'pending' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400' :
                                                        'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                                                    }`}>
                                                    {transaction.status}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                                <div className="flex items-center gap-1">
                                                    <CalendarIcon className="h-4 w-4" />
                                                    {new Date(transaction.createdAt).toLocaleDateString()}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Withdrawals Tab */}
                {activeTab === 'withdrawals' && (
                    <div className="space-y-6">
                        {wallet?.currentBalance && wallet.currentBalance > 0 && (
                            <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                                <div className="flex justify-between items-center mb-6">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-green-100 dark:bg-green-900/20 rounded-lg">
                                            <ArrowUpTrayIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
                                        </div>
                                        <div>
                                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Request Withdrawal</h2>
                                            <p className="text-sm text-gray-600 dark:text-gray-400">Withdraw your available balance</p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => setShowWithdrawalForm(!showWithdrawalForm)}
                                        disabled={!wallet?.bankDetails?.isVerified}
                                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-all duration-200 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <ArrowUpTrayIcon className="h-4 w-4" />
                                        {showWithdrawalForm ? 'Cancel' : 'Request Withdrawal'}
                                    </button>
                                </div>

                                {!wallet?.bankDetails?.isVerified && (
                                    <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl">
                                        <div className="flex items-center gap-2">
                                            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                                            <p className="text-sm text-yellow-800 dark:text-yellow-400">
                                                Bank details must be verified before requesting withdrawal. Please update your bank details first.
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {wallet?.bankDetails?.isVerified && (
                                    <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <CheckCircleIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
                                                <p className="text-sm text-green-800 dark:text-green-400 font-medium">
                                                    Bank details verified - Ready for withdrawal
                                                </p>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-sm text-green-700 dark:text-green-300">
                                                    {wallet.bankDetails.bankName} • {wallet.bankDetails.accountNumber}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {showWithdrawalForm && wallet?.bankDetails?.isVerified && (
                                    <form onSubmit={handleWithdrawalSubmit} className="space-y-6 bg-gray-50 dark:bg-gray-700/50 p-6 rounded-xl">
                                        {/* Bank Details Display */}
                                        <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-600">
                                            <h4 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                                                <BuildingLibraryIcon className="h-5 w-5" />
                                                Withdrawal Account Details
                                            </h4>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Bank Name</label>
                                                    <p className="text-sm text-gray-900 dark:text-white font-medium">{wallet.bankDetails.bankName}</p>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Account Number</label>
                                                    <p className="text-sm text-gray-900 dark:text-white font-mono">{wallet.bankDetails.accountNumber}</p>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">IFSC Code</label>
                                                    <p className="text-sm text-gray-900 dark:text-white font-mono">{wallet.bankDetails.ifscCode}</p>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Account Holder</label>
                                                    <p className="text-sm text-gray-900 dark:text-white font-medium">{wallet.bankDetails.accountHolderName}</p>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Amount Input */}
                                        <div>
                                            <label htmlFor="amount" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                                                Withdrawal Amount (₹)
                                            </label>
                                            <div className="relative">
                                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                    <CurrencyDollarIcon className="h-5 w-5 text-gray-400" />
                                                </div>
                                                <input
                                                    type="number"
                                                    id="amount"
                                                    value={withdrawalData.amount}
                                                    onChange={(e) => setWithdrawalData(prev => ({ ...prev, amount: e.target.value }))}
                                                    min="5"
                                                    max={(wallet?.currentBalance || 0) - (wallet?.withdrawalRequests?.filter(req => req.status === 'pending' || req.status === 'approved').reduce((sum, req) => sum + req.amount, 0) || 0)}
                                                    step="1"
                                                    required
                                                    className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                                                    placeholder="Enter withdrawal amount"
                                                />
                                            </div>
                                            <div className="mt-2 space-y-1">
                                                <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1">
                                                    <CheckCircleIcon className="h-4 w-4 text-green-500" />
                                                    Available Balance: ₹{wallet?.currentBalance?.toLocaleString() || '0'}
                                                </p>
                                                <p className="text-xs text-gray-500 dark:text-gray-500">
                                                    Minimum withdrawal: ₹5 • Maximum withdrawal: ₹{wallet?.currentBalance?.toLocaleString() || '0'}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="flex justify-end pt-4">
                                            <button
                                                type="submit"
                                                disabled={!withdrawalData.amount || parseFloat(withdrawalData.amount) > ((wallet?.currentBalance || 0) - (wallet?.withdrawalRequests?.filter(req => req.status === 'pending' || req.status === 'approved').reduce((sum, req) => sum + req.amount, 0) || 0)) || parseFloat(withdrawalData.amount) < 5}
                                                className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-all duration-200 shadow-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <ArrowUpTrayIcon className="h-4 w-4" />
                                                Submit Withdrawal Request
                                            </button>
                                        </div>
                                    </form>
                                )}
                            </div>
                        )}

                        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                            <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                                        <ArrowUpTrayIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                                    </div>
                                    <div>
                                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Withdrawal History</h2>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">Track your withdrawal requests and status</p>
                                    </div>
                                </div>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                    <thead className="bg-gray-50 dark:bg-gray-700">
                                        <tr>
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                Amount
                                            </th>
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                Status
                                            </th>
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                Requested Date
                                            </th>
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                Processed Date
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                        {withdrawals.map((withdrawal: WithdrawalRequest) => (
                                            <tr key={withdrawal._id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className="text-lg font-bold text-gray-900 dark:text-white">
                                                        ₹{withdrawal.amount?.toLocaleString() || '0'}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${withdrawal.status === 'processed' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' :
                                                        withdrawal.status === 'approved' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400' :
                                                            withdrawal.status === 'pending' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400' :
                                                                'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                                                        }`}>
                                                        {withdrawal.status}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                                    <div className="flex items-center gap-1">
                                                        <CalendarIcon className="h-4 w-4" />
                                                        {new Date(withdrawal.requestedAt).toLocaleDateString()}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                                    {withdrawal.processedAt ? (
                                                        <div className="flex items-center gap-1">
                                                            <CalendarIcon className="h-4 w-4" />
                                                            {new Date(withdrawal.processedAt).toLocaleDateString()}
                                                        </div>
                                                    ) : (
                                                        <span className="text-gray-400">-</span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {/* Bank Details Tab */}
                {activeTab === 'bank' && (
                    <div className="space-y-6">
                        <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                            <div className="flex justify-between items-center mb-6">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                                        <BuildingLibraryIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                                    </div>
                                    <div>
                                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Bank Details</h2>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">Manage your bank account information</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setShowBankForm(!showBankForm)}
                                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all duration-200 shadow-sm"
                                >
                                    <BuildingLibraryIcon className="h-4 w-4" />
                                    {showBankForm ? 'Cancel' : 'Update Bank Details'}
                                </button>
                            </div>

                            {wallet?.bankDetails && (
                                <div className="bg-gray-50 dark:bg-gray-700/50 p-6 rounded-xl mb-6">
                                    <h3 className="text-md font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                                        <UserIcon className="h-5 w-5" />
                                        Current Bank Details
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Account Number</label>
                                            <p className="text-sm text-gray-900 dark:text-white font-medium">{wallet.bankDetails.accountNumber}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Bank Name</label>
                                            <p className="text-sm text-gray-900 dark:text-white font-medium">{wallet.bankDetails.bankName}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">IFSC Code</label>
                                            <p className="text-sm text-gray-900 dark:text-white font-medium">{wallet.bankDetails.ifscCode}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Branch Name</label>
                                            <p className="text-sm text-gray-900 dark:text-white font-medium">{wallet.bankDetails.branchName}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Account Holder Name</label>
                                            <p className="text-sm text-gray-900 dark:text-white font-medium">{wallet.bankDetails.accountHolderName}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Verification Status</label>
                                            <span className={`inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded-full ${wallet.bankDetails.isVerified ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
                                                }`}>
                                                {wallet.bankDetails.isVerified ? (
                                                    <CheckCircleIcon className="h-3 w-3" />
                                                ) : (
                                                    <ExclamationTriangleIcon className="h-3 w-3" />
                                                )}
                                                {wallet.bankDetails.isVerified ? 'Verified' : 'Pending Verification'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {showBankForm && (
                                <form onSubmit={handleBankDetailsSubmit} className="space-y-6 bg-gray-50 dark:bg-gray-700/50 p-6 rounded-xl">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label htmlFor="bankAccountNumber" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                                                Account Number
                                            </label>
                                            <input
                                                type="text"
                                                id="bankAccountNumber"
                                                value={bankData.accountNumber}
                                                onChange={(e) => setBankData(prev => ({ ...prev, accountNumber: e.target.value }))}
                                                required
                                                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                                                placeholder="Enter account number"
                                            />
                                        </div>

                                        <div>
                                            <label htmlFor="bankBankName" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                                                Bank Name
                                            </label>
                                            <input
                                                type="text"
                                                id="bankBankName"
                                                value={bankData.bankName}
                                                onChange={(e) => setBankData(prev => ({ ...prev, bankName: e.target.value }))}
                                                required
                                                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                                                placeholder="Enter bank name"
                                            />
                                        </div>

                                        <div>
                                            <label htmlFor="bankIfscCode" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                                                IFSC Code
                                            </label>
                                            <input
                                                type="text"
                                                id="bankIfscCode"
                                                value={bankData.ifscCode}
                                                onChange={(e) => setBankData(prev => ({ ...prev, ifscCode: e.target.value }))}
                                                required
                                                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                                                placeholder="Enter IFSC code"
                                            />
                                        </div>

                                        <div>
                                            <label htmlFor="branchName" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                                                Branch Name
                                            </label>
                                            <input
                                                type="text"
                                                id="branchName"
                                                value={bankData.branchName}
                                                onChange={(e) => setBankData(prev => ({ ...prev, branchName: e.target.value }))}
                                                required
                                                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                                                placeholder="Enter branch name"
                                            />
                                        </div>

                                        <div className="md:col-span-2">
                                            <label htmlFor="bankAccountHolderName" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                                                Account Holder Name
                                            </label>
                                            <input
                                                type="text"
                                                id="bankAccountHolderName"
                                                value={bankData.accountHolderName}
                                                onChange={(e) => setBankData(prev => ({ ...prev, accountHolderName: e.target.value }))}
                                                required
                                                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                                                placeholder="Enter account holder name"
                                            />
                                        </div>
                                    </div>

                                    <div className="flex justify-end pt-4">
                                        <button
                                            type="submit"
                                            className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all duration-200 shadow-sm font-semibold"
                                        >
                                            <BuildingLibraryIcon className="h-4 w-4" />
                                            Update Bank Details
                                        </button>
                                    </div>
                                </form>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default WalletManagement;
